<script lang="ts">
import type { PropType } from '@mimir/web/compat/vueCompat';
import { defineComponent } from '@mimir/web/compat/vueCompat';
import { VCol, VIcon, VRow, VTextField } from 'vuetify/lib';
import { makeInputVariantProps, makeValidationProps } from '@mimir/web/compat/components/props';
import type { InputVariant } from '@mimir/web/compat/components/props';
import {
  toV2InputAttrs,
  toV2InputListeners,
  toV2InputSlotName,
  toV2InputVariant,
  toV2ValidateOn,
} from '@mimir/web/compat/components/vuetify2Conversions';

const isVue2 = !!import.meta.env.VUE_APP_V2;

// Not passed to Vuetify 2: look flags, which `variant` alone decides, and attrs the wrapper handles itself or drops.
const droppedV2Attrs = ['filled', 'outlined', 'solo', 'solo-inverted', 'soloInverted', 'prepend-inner-icon', 'density', 'success', 'success-messages'];

export default defineComponent({
  name: 'MTextField',
  components: {
    VCol,
    VIcon,
    VRow,
    VTextField,
  },
  inheritAttrs: false,
  props: {
    ...makeInputVariantProps(),
    ...makeValidationProps(),
    // Vue 2 prop
    value: {
      type: [String, Number] as PropType<string | number>,
      default: ''
    },
    // Vue 3 prop
    modelValue: {
      type: [String, Number] as PropType<string | number>,
      default: ''
    },
    label: {
      type: String as PropType<string>,
      default: undefined
    },
    validatedTooltip: {
      type: String as PropType<string>,
      default: undefined
    },
    prependIcon: {
      type: String as PropType<string>,
      default: undefined
    },
    tooltipPosition: {
      type: String as PropType<string>,
      default: undefined
    },
    validated: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    isPassword: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    showReveal: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    clickable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    type: {
      type: String as PropType<string>,
      default: 'text'
    },
  },
  data() {
    return {
      isFocused: false,
      showingSecret: false,
      isVue2
    };
  },
  computed: {
    internalValue: {
      get(): string | number {
        return this.modelValue !== '' ? this.modelValue : this.value;
      },
      set(val: string | number) {
        this.$emit('input', val); // Vue 2
        this.$emit('update:modelValue', val); // Vue 3
      }
    },
    resolvedVariant(): InputVariant {
      return this.variant ?? 'solo';
    },
    computedFilled(): boolean {
      return this.resolvedVariant === 'filled';
    },
    computedPrependIcon(): string | undefined {
      return this.prependIcon || (this.$attrs['prepend-inner-icon'] as string);
    },
    vue2Bindings(): Record<string, any> {
      const attrs = toV2InputAttrs(this.$attrs);
      for (const name of droppedV2Attrs) {
        delete attrs[name];
      }
      return {
        ...attrs,
        value: this.internalValue,
        rules: this.rules,
        errorMessages: this.errorMessages,
        ...toV2InputVariant(this.resolvedVariant),
        ...toV2ValidateOn(this.validateOn),
      };
    },
    vue2Listeners(): Record<string, any> {
      return {
        ...toV2InputListeners(this.$listeners),
        input: (value: any) => { this.internalValue = value; }
      };
    },
    proxyType(): string {
      if (!this.isPassword) {
        return this.type;
      }
      return this.showingSecret ? this.type : 'password';
    },
    showPasswordTooltip(): string {
      return this.showingSecret ? 'Hide' : 'Reveal';
    }
  },
  methods: {
    focus() {
      (this.$refs.inner as any).focus();
    },
    toV2InputSlotName,
  },
});

</script>

<template>
  <VRow
    no-gutters
    class="profile-row"
  >
    <VCol
      v-if="label && !computedFilled"
      cols="12"
      :class="['profile-label', isFocused && 'focused', computedPrependIcon && 'pl-8']"
    >
      <span
        v-if="!$scopedSlots['infotext']"
        v-text="label"
      />
      <InfoBubble
        v-else
        :tooltipPosition="tooltipPosition"
      >
        <span v-text="label" />
        <template #infotext>
          <slot name="infotext" />
        </template>
      </InfoBubble>
    </VCol>
    <VCol cols="12">
      <!-- Vue 2 Mode: Accept v3 props, transform to v2 for VTextField -->
      <VTextField
        v-if="isVue2"
        v-bind="vue2Bindings"
        ref="inner"
        :class="['m-text-field', 'm-common', clickable && 'clickable']"
        hide-details="auto"
        autocomplete="new-password"
        flat
        :type="proxyType"
        :loader-height="1"
        :prepend-icon="computedPrependIcon"
        :aria-label="$attrs['aria-label'] || label"
        :label="computedFilled ? label : ''"
        v-on="vue2Listeners"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
        <template
          v-if="isPassword && showReveal"
          #append
        >
          <VIcon
            v-tooltip="showPasswordTooltip"
            class="mr-1"
            style="cursor:pointer"
            @click="showingSecret = !showingSecret"
          >{{ showingSecret ? 'visibility_off' : 'visibility' }}</VIcon>
          <VIcon
            v-if="validated"
            v-tooltip="{ disabled: !validatedTooltip, text: validatedTooltip }"
            color="success"
            class="ml-1"
          >check</VIcon>
        </template>

        <template
          v-for="(_, scopedSlotName) in $scopedSlots"
          #[toV2InputSlotName(String(scopedSlotName))]="slotData"
        >
          <slot
            :name="scopedSlotName"
            v-bind="slotData"
          />
        </template>
        <template
          v-for="(_, slotName) in $slots"
          #[toV2InputSlotName(String(slotName))]
        >
          <slot :name="slotName" />
        </template>
      </VTextField>

      <!-- Vue 3 Mode: Use native v3 props and slots directly -->
      <VTextField
        v-else
        v-bind="$attrs"
        ref="inner"
        :class="['m-text-field', 'm-common', clickable && 'clickable']"
        hide-details="auto"
        autocomplete="new-password"
        :type="proxyType"
        :variant="resolvedVariant"
        :prepend-icon="prependIcon"
        :modelValue="internalValue"
        :rules="rules"
        :validate-on="validateOn"
        :error-messages="errorMessages"
        :aria-label="$attrs['aria-label'] || label"
        :label="computedFilled ? label : ''"
        @change="$emit('change', $event.target?.value ?? internalValue)"
        @update:modelValue="internalValue = $event"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
        <template
          v-if="isPassword && showReveal"
          #append-inner
        >
          <VIcon
            v-tooltip="showPasswordTooltip"
            class="mr-1"
            style="cursor:pointer"
            @click="showingSecret = !showingSecret"
          >{{ showingSecret ? 'visibility_off' : 'visibility' }}</VIcon>
          <VIcon
            v-if="validated"
            v-tooltip="{ disabled: !validatedTooltip, text: validatedTooltip }"
            color="success"
            class="ml-1"
          >check</VIcon>
        </template>
        <template
          v-for="(_, slotName) in $slots"
          #[slotName]="slotData"
        >
          <slot
            :name="slotName"
            v-bind="slotData || {}"
          />
        </template>
      </VTextField>
    </VCol>
  </VRow>
</template>

<style scoped lang="stylus">
:deep(.v-input--is-disabled) .v-input__slot,
:deep(.v-input--is-readonly):not(.clickable) .v-input__slot
  opacity: .5
  input
    color: var(--v-default-base)
  .profile-row
    font-size: 14px
    white-space: pre-wrap
    overflow-wrap: break-word
    .profile-label
      font-size: 12px
      color: var(--v-primary-base)
      opacity: 0.8
      &.focused,
      .high-contrast
        color: var(--v-strong-base)
        opacity: 1
.clickable
  :deep(input)
    cursor: pointer
:deep(.v-text-field--filled)
  .v-input__append-inner
    margin-top: 7px
  .v-input__prepend-outer
    margin-top: 15px
  .v-input__slot
    padding: 0px 4px !important
    min-height: 36px !important
    border-radius: 2px !important
    background: var(--v-ternaryBackground-base) !important
    input
      color: var(--v-default-base)
  .v-label
    color: var(--v-default-base)
    top: 11px
    font-size: 13px !important
    line-height: 16px
  .v-label--active
    color: var(--v-default-base)
    top: 9px
    font-size: 11px !important
  input
    padding: 0 !important
    margin-top: 10px !important
    max-height: 36px !important
  .v-input__slot:before, .v-input__slot:after
    display: none
</style>
